'use client';
import { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { TechnicalDetailsTable } from './technicalDetailsTable';
import * as FA from 'react-icons/fa';

const PRIMARY = 'var(--primary-color)';
const STAR_COLOR = '#f59e0b';

// Returns a semi-transparent version of the primary color
const pa = (pct) => `color-mix(in srgb, var(--primary-color) ${pct}%, transparent)`;

const MiniStars = ({ rating = 0 }) => {
    const filled = Math.round(rating);
    return (
        <div style={{ display: 'flex', gap: '2px' }}>
            {[1, 2, 3, 4, 5].map(s => (
                <i
                    key={s}
                    className={`pi ${s <= filled ? 'pi-star-fill' : 'pi-star'}`}
                    style={{ fontSize: '0.65rem', color: s <= filled ? STAR_COLOR : '#d1d5db' }}
                />
            ))}
        </div>
    );
};

const resolveIcon = (iconName) => {
    if (!iconName) return null;
    if (FA[iconName]) return FA[iconName];
    const faKey = `Fa${iconName.charAt(0).toUpperCase()}${iconName.slice(1)}`;
    return FA[faKey] || null;
};

const FeatureCard = ({ feature }) => {
    const IconComp = resolveIcon(feature.icon);
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '14px 16px',
            background: '#fff', border: `1.5px solid ${pa(12)}`,
            borderRadius: '14px', width: '100%',
            boxShadow: `0 2px 8px ${pa(5)}`,
        }}>
            <div style={{
                width: '52px', height: '52px', borderRadius: '14px', flexShrink: 0,
                background: pa(8), border: `1.5px solid ${pa(18)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {IconComp ? (
                    <IconComp style={{ fontSize: '1.5rem', color: PRIMARY }} />
                ) : (
                    <i className={`pi pi-${feature.icon || 'star'}`} style={{ fontSize: '1.25rem', color: PRIMARY }} />
                )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.82rem', color: '#1e293b', lineHeight: 1.3 }}>
                    {feature.title}
                </p>
                {feature.info && (
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', fontWeight: 400, lineHeight: 1.5 }}>
                        {feature.info}
                    </p>
                )}
            </div>
        </div>
    );
};

const EmptyState = ({ icon, message }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '12px', padding: '48px 16px', color: '#94a3b8',
    }}>
        <i className={`pi ${icon}`} style={{ fontSize: '2rem', color: '#cbd5e1' }} />
        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 500 }}>{message}</p>
    </div>
);

const LoadingState = () => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '12px', padding: '48px 16px',
    }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.8rem', color: PRIMARY }} />
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>Loading…</p>
    </div>
);

const NOTE = 'Note: If your device has physical damage or short circuit, it will be handled as out-of-warranty and charged accordingly.';

// Same treatment as the "User Manual" link on the product details page (AICircle) —
// links to the full-view PDF page instead of the raw file.
const UserManualLink = ({ slug }) => (
    <a
        href={`/user-manual/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 12px', borderRadius: '10px',
            border: `1px solid ${pa(18)}`, background: pa(5),
            textDecoration: 'none',
        }}
    >
        <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: '#fee2e2', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }}>
            <i className="pi pi-file-pdf" style={{ color: '#dc2626', fontSize: '0.9rem' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                View User Manual
            </p>
            <p style={{ margin: 0, fontSize: '0.68rem', color: '#64748b' }}>PDF document</p>
        </div>
        <i className="pi pi-external-link" style={{ color: PRIMARY, fontSize: '0.8rem', flexShrink: 0 }} />
    </a>
);

const TechTab = ({ data, loading }) => {
    if (loading) return <LoadingState />;
    if (!data || data.length === 0) return <EmptyState icon="pi-table" message="No technical specifications available." />;
    return (
        <>
            <div style={{ marginBottom: '12px' }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    background: pa(7), color: PRIMARY,
                    border: `1px solid ${pa(18)}`, borderRadius: '8px',
                    padding: '4px 12px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                    <i className="pi pi-table" style={{ fontSize: '0.65rem' }} />
                    {data.length} Specification{data.length !== 1 ? 's' : ''}
                </span>
            </div>
            <TechnicalDetailsTable data={data} />
            <div style={{
                marginTop: '16px', padding: '10px 12px',
                background: '#fff7f7', border: '1px solid #fecaca',
                borderRadius: '8px', fontSize: '0.7rem', color: '#ef4444', lineHeight: 1.6,
            }}>
                {NOTE}
            </div>
        </>
    );
};

const FeaturesTab = ({ data, loading }) => {
    if (loading) return <LoadingState />;
    if (!data || data.length === 0) return <EmptyState icon="pi-star" message="No features listed for this product." />;
    const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
        <>
            <div style={{ marginBottom: '12px' }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    background: '#fef3c714', color: '#d97706',
                    border: '1px solid #fde68a', borderRadius: '8px',
                    padding: '4px 12px', fontSize: '0.72rem', fontWeight: 700,
                }}>
                    <i className="pi pi-star-fill" style={{ fontSize: '0.65rem' }} />
                    {sorted.length} Key Feature{sorted.length !== 1 ? 's' : ''}
                </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sorted.map((f, i) => <FeatureCard key={f._id || i} feature={f} />)}
            </div>
        </>
    );
};

export const DetailsView = ({ isDetails, setIsDetails }) => {
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (isDetails) setActiveTab(0);
    }, [isDetails?.title]);

    const {
        techDetails, features, title, coverImage, slug, userManualUrl,
        avgRating = 0, reviewCount = 0, loading = false,
    } = isDetails || {};

    const tabs = [
        { label: 'Tech Specs', icon: 'pi-list', count: techDetails?.length ?? 0 },
        { label: 'Features',   icon: 'pi-star',  count: features?.length ?? 0 },
    ];

    const sidebarHeader = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
                width: '28px', height: '28px', borderRadius: '8px',
                background: PRIMARY,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                <i className="pi pi-list" style={{ fontSize: '0.75rem', color: '#fff' }} />
            </div>
            <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b' }}>
                Product Specifications
            </span>
        </div>
    );

    return (
        <Sidebar
            visible={!!isDetails}
            onHide={() => setIsDetails(false)}
            position="right"
            style={{ width: '440px', maxWidth: '96vw' }}
            header={sidebarHeader}
            pt={{
                header: {
                    style: {
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        padding: '12px 16px',
                    },
                },
                content: { style: { padding: 0, overflow: 'hidden' } },
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

                {/* ── Product hero ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 16px',
                    background: '#fff',
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                }}>
                    {coverImage ? (
                        <div style={{
                            width: '60px', height: '60px', borderRadius: '12px',
                            background: '#fff', border: `1.5px solid ${pa(25)}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, overflow: 'hidden', padding: '4px',
                            boxShadow: `0 4px 12px ${pa(13)}`,
                        }}>
                            <img src={coverImage} alt={title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                    ) : (
                        <div style={{
                            width: '60px', height: '60px', borderRadius: '12px',
                            background: pa(9), border: `1.5px solid ${pa(25)}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <i className="pi pi-box" style={{ color: PRIMARY, fontSize: '1.4rem' }} />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                            margin: '0 0 7px', fontWeight: 700, fontSize: '0.85rem', color: '#1e293b',
                            display: '-webkit-box', WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                            {title}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {avgRating > 0 && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    background: pa(9), color: PRIMARY,
                                    border: `1px solid ${pa(25)}`, borderRadius: '20px',
                                    padding: '3px 10px', fontSize: '0.7rem', fontWeight: 800,
                                }}>
                                    <MiniStars rating={avgRating} />
                                    {Number(avgRating).toFixed(1)}
                                </span>
                            )}
                            {reviewCount > 0 && (
                                <span style={{
                                    background: '#f1f5f9', color: '#64748b',
                                    border: '1px solid #e2e8f0', borderRadius: '20px',
                                    padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600,
                                }}>
                                    {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── User manual link ── */}
                {userManualUrl && slug && (
                    <div style={{ padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                        <UserManualLink slug={slug} />
                    </div>
                )}

                {/* ── Fixed Tab Bar ── */}
                <div style={{
                    flexShrink: 0, background: '#fff',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex', padding: '0 4px',
                }}>
                    {tabs.map((tab, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveTab(i)}
                            style={{
                                flex: 1, padding: '11px 8px', border: 'none',
                                background: 'transparent', cursor: 'pointer',
                                borderBottom: activeTab === i ? `2px solid ${PRIMARY}` : '2px solid transparent',
                                marginBottom: '-2px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                color: activeTab === i ? PRIMARY : '#64748b',
                                fontWeight: activeTab === i ? 700 : 500,
                                fontSize: '0.82rem',
                                transition: 'all 0.15s',
                            }}
                        >
                            <i className={`pi ${tab.icon}`} style={{ fontSize: '0.8rem' }} />
                            {tab.label}
                            {!loading && tab.count > 0 && (
                                <span style={{
                                    background: activeTab === i ? PRIMARY : '#e2e8f0',
                                    color: activeTab === i ? '#fff' : '#64748b',
                                    borderRadius: '10px', padding: '1px 7px',
                                    fontSize: '0.62rem', fontWeight: 700, minWidth: '18px',
                                    textAlign: 'center',
                                }}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* ── Scrollable Content ── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    {activeTab === 0 ? (
                        <TechTab data={techDetails} loading={loading} />
                    ) : (
                        <FeaturesTab data={features} loading={loading} />
                    )}
                </div>
            </div>
        </Sidebar>
    );
};
